import { useEffect, useState, useContext } from "react";

import {
  getDashboardSummary,
  getDailySales,
  getMonthlySales,
  getTopProducts,
  getOrderStatus,
  getProfit,
  getTotalProfit
} from "../../services/dashboardApi";

import {
  downloadPDF,
  downloadExcel,
  sendReportEmail
} from "../../services/exportApi";

import Sidebar from "../../components/common/Sidebar";
import Navbar from "../../components/common/Navbar";
import { AuthContext } from "../../context/AuthContext";
import "./Reports.css";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell
} from "recharts";

const Reports = () => {
  const { user } = useContext(AuthContext);

  const [summary, setSummary] = useState({});
  const [daily, setDaily] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [orderStatus, setOrderStatus] = useState([]);
  const [profit, setProfit] = useState([]);
  const [totalProfit, setTotalProfit] = useState({});

  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const loadData = async () => {
    try {
      const [
        summaryRes,
        dailyRes,
        monthlyRes,
        topRes,
        statusRes,
        profitRes,
        totalProfitRes
      ] = await Promise.all([
        getDashboardSummary(),
        getDailySales(),
        getMonthlySales(),
        getTopProducts(),
        getOrderStatus(),
        getProfit(),
        getTotalProfit()
      ]);

      setSummary(summaryRes?.data ?? {});
      setDaily(dailyRes?.data ?? []);
      setMonthly(monthlyRes?.data ?? []);
      setTopProducts(topRes?.data ?? []);
      setOrderStatus(statusRes?.data ?? []);
      setProfit(profitRes?.data ?? []);
      setTotalProfit(totalProfitRes?.data ?? {});
    } catch (err) {
      console.log("Reports Error:", err?.response?.data || err.message);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const COLORS = ["#3b82f6", "#22c55e", "#ef4444", "#a855f7", "#f59e0b"];

  const handlePDF = async () => {
    try {
      setLoading(true);
      const blob = await downloadPDF({ from, to });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "report.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };

  const handleExcel = async () => {
    try {
      setLoading(true);
      const blob = await downloadExcel({ from, to });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "report.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } finally {
      setLoading(false);
    }
  };

  const handleEmail = async () => {
    try {
      setLoading(true);
      await sendReportEmail({ email, from, to });
      alert("📧 Report sent successfully!");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-container">
      <Sidebar role={user?.role} />

      <div className="admin-main">
        <Navbar />

        <div className="admin-content">
          <h2>📊 Sales Reports</h2>

          {/* ===== SUMMARY ===== */}
          <div className="report-grid">
            <div className="report-card">
              <h3>Total Orders</h3>
              <p>{summary?.total_orders || 0}</p>
            </div>

            <div className="report-card">
              <h3>Total Revenue</h3>
              <p>₹{summary?.total_revenue || 0}</p>
            </div>

            <div className="report-card">
              <h3>Avg Order</h3>
              <p>₹{summary?.average_order_value || 0}</p>
            </div>

            <div className="report-card">
              <h3>Total Profit</h3>
              <p>₹{totalProfit?.total_profit || 0}</p>
            </div>
          </div>

          {/* ===== CHART GRID ===== */}
          <div className="charts-grid">

            <div className="chart-box">
              <h3>📈 Daily Sales</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={daily}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke="#3b82f6" />
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-box">
              <h3>📊 Monthly Sales</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-box">
              <h3>🏆 Top Products</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={topProducts}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="product_name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sold" fill="#f59e0b" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-box">
              <h3>📦 Order Status</h3>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={orderStatus}
                    dataKey="count"
                    nameKey="status"
                    outerRadius={80}
                    label
                  >
                    {orderStatus.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-box full-width">
              <h3>💰 Profit Trend</h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={profit}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="profit" stroke="#ef4444" />
                </LineChart>
              </ResponsiveContainer>
            </div>

          </div>

          {/* ===== EXPORT ===== */}
          <div className="export-box">
            <h3>📦 Export Reports</h3>

            <div className="export-row">
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />

              <button onClick={handlePDF} disabled={loading}>📄 PDF</button>
              <button onClick={handleExcel} disabled={loading}>📊 Excel</button>

              <input
                type="email"
                placeholder="Enter email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />

              <button onClick={handleEmail} disabled={loading}>📧 Send</button>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Reports;